/**
 * FOTOĞRAF YÜKLEME — sıkıştırma + gönderim, tek yerde.
 *
 * NEDEN AYRI DOSYA: `FotoEkle` bir düğme bileşeni ve bir `jobId` bekliyor.
 * İş açma ekranında iş henüz YOKTUR — dosya önce seçilir, iş açıldıktan sonra
 * yüklenir. Sıkıştırmayı orada ikinci kez yazmak, iki farklı boyut/kalite
 * ayarının sessizce ayrışması demekti.
 *
 * ⚠️ Çekilme zamanı CİHAZDAN gönderilir. Offline'da sunucu saati yanlıştır ve
 *    kanıt zincirinde "ne zaman çekildi" sorusunun cevabı budur (K8).
 */

const MAKS_KENAR = 1600;
const KALITE = 0.75;

/**
 * İSTEMCİDE SIKIŞTIRMA (T2): saha telefonları 4000px+ fotoğraf üretiyor,
 * bağlantı zayıf. 1600px / 0.75 kalite ile ~5 MB dosya ~400 KB'a düşüyor —
 * fabrikada bu, yüklemenin bitip bitmemesi demek.
 */
export async function sikistir(dosya: File): Promise<Blob> {
  // HEIC gibi tarayıcının çözemediği formatlarda olduğu gibi gönder.
  if (!/^image\/(jpeg|png|webp)$/.test(dosya.type)) return dosya;

  const bitmap = await createImageBitmap(dosya).catch(() => null);
  if (!bitmap) return dosya;

  const oran = Math.min(1, MAKS_KENAR / Math.max(bitmap.width, bitmap.height));
  if (oran === 1 && dosya.size < 1_000_000) {
    bitmap.close();
    return dosya;
  }

  const c = document.createElement('canvas');
  c.width = Math.round(bitmap.width * oran);
  c.height = Math.round(bitmap.height * oran);
  c.getContext('2d')!.drawImage(bitmap, 0, 0, c.width, c.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((res) => c.toBlob(res, 'image/jpeg', KALITE));
  return blob ?? dosya;
}

/**
 * KABUL EDİLEN TÜRLER — görsel VE PDF.
 *
 * ⚠️ PDF müşteri isteğiyle geldi (06.09.2026, Akçansa): bir iş izni beş sayfa
 *    ve tek tek fotoğraflamak sahada zaman kaybı. Sunucudaki beyaz liste bunun
 *    ikizi ([foto.controller.ts] UZANTI); ikisi ayrışırsa kullanıcı seçebildiği
 *    bir dosyada "desteklenmeyen tür" hatası alır. Adım ekranları ve iş açma
 *    ekranı aynı sabiti okur; iş açma 09.10.2026'ya kadar `image/*` ile
 *    ayrışmıştı ve şef iş emrini PDF olarak yükleyemiyordu.
 */
export const BELGE_KABUL = 'image/*,application/pdf';

/** PDF sıkıştırılmaz ve fotoğraf gibi gösterilmez — tek yerde sorulur. */
export const pdfMi = (dosya: Blob): boolean => dosya.type === 'application/pdf';

/**
 * Gönderilecek dosya adı.
 *
 * ⚠️ ESKİDEN HER ŞEY `.jpg` OLUYORDU (`jpegAdi`). Sıkıştırma çıktısı gerçekten
 *    JPEG olduğu için doğruydu — ta ki PDF yüklenene kadar. `izin.pdf` adı
 *    `izin.jpg` diye gidince sunucu mime'a bakıp kabul eder ama depodaki nesne
 *    yanlış uzantıyla durur ve elle inceleyen kişi belgeyi açamaz.
 */
export function yuklemeAdi(dosya: File, gonderilen: Blob): string {
  const govde = dosya.name.replace(/\.[^.]+$/, '') || 'belge';
  return pdfMi(gonderilen) ? `${govde}.pdf` : `${govde}.jpg`;
}

export type YuklemeSonucu = { ok: true; kuyrukta: boolean } | { ok: false; hata: string };

/**
 * Bir adıma fotoğraf yükler. Çevrimdışıysa kuyruğa alır ve `kuyrukta: true` döner —
 * saha kullanıcısı hata görmemeli, bağlantı gelince gider (T1).
 */
export async function fotoYukle(
  jobId: string,
  step: string,
  dosya: File,
): Promise<YuklemeSonucu> {
  try {
    const kucuk = await sikistir(dosya);
    const cekilmeZamani = new Date().toISOString();

    if (!navigator.onLine) {
      const { fotoKuyrugaAl } = await import('./outbox');
      await fotoKuyrugaAl({ jobId, step, blob: kucuk, dosyaAdi: yuklemeAdi(dosya, kucuk), cekilmeZamani });
      return { ok: true, kuyrukta: true };
    }

    const fd = new FormData();
    fd.append('dosya', kucuk, yuklemeAdi(dosya, kucuk));
    fd.append('step', step);
    fd.append('cekilmeZamani', cekilmeZamani);

    const r = await fetch(`/api/isler/${jobId}/foto`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    if (!r.ok) {
      const g = (await r.json().catch(() => null)) as { hata?: string } | null;
      return { ok: false, hata: g?.hata ?? `HTTP ${r.status}` };
    }
    return { ok: true, kuyrukta: false };
  } catch (e) {
    return { ok: false, hata: e instanceof Error ? e.message : 'bilinmeyen hata' };
  }
}

/**
 * ÇOK SAYFALI BELGE — seçilen dosyaları SIRAYLA yükler.
 *
 * ⚠️ NEDEN SIRAYLA, paralel değil: sayfa sırası `fotoNo` ile belirleniyor ve
 *    o numara sunucuda `autoincrement`. Paralel gönderimde 5 sayfalık iznin
 *    numaraları karışır, ekranda ve günlük raporda sayfa 3 sayfa 1'in önüne
 *    geçer. Kanıtın okunabilirliği hızdan önce gelir.
 *
 * ⚠️ BİRİ DÜŞERSE ÖTEKİLER DEVAM EDER ve hangisinin düştüğü geri döner.
 *    "5 sayfa seçtim, biri gitmedi" durumunda kullanıcı hangisini yeniden
 *    yükleyeceğini bilmeli; toplu bir "olmadı" mesajı onu baştan başlatırdı.
 */
export async function fotoYukleCoklu(
  jobId: string,
  step: string,
  dosyalar: readonly File[],
  ilerleme?: (biten: number, toplam: number) => void,
): Promise<{ basarili: number; kuyrukta: number; hatalar: { ad: string; hata: string }[] }> {
  let basarili = 0;
  let kuyrukta = 0;
  const hatalar: { ad: string; hata: string }[] = [];

  for (const [i, dosya] of dosyalar.entries()) {
    const sonuc = await fotoYukle(jobId, step, dosya);
    if (sonuc.ok) {
      basarili++;
      if (sonuc.kuyrukta) kuyrukta++;
    } else {
      hatalar.push({ ad: dosya.name, hata: sonuc.hata });
    }
    ilerleme?.(i + 1, dosyalar.length);
  }

  return { basarili, kuyrukta, hatalar };
}

